(function () {
  'use strict';

  /**
   * Column bar-wave band — binary light/dark only (no color blending).
   * Solid dark canvas; light bars with variable height create the shape fade.
   * Thin bars at the top (footer edge), thick bars at the bottom (page light).
   */
  var CONFIG = {
    /** Band height in CSS pixels — single source of truth (applied to the DOM on init). */
    HEIGHT: 600,

    /** Fixed column counts per breakpoint (overrides width-based column calc). */
    COLS: {
      DESKTOP: 11,
      LARGE: 9,
      TABLET: 5,
      MOBILE: 3,
    },

    /** Align with theme layout breakpoints (--container-max for large). */
    BREAKPOINTS: {
      TABLET_MAX: 990,
      MOBILE_MAX: 768,
      LARGE_MAX: 1369,
    },

    /** Row cell height in CSS pixels. */
    CELL_SIZE: 100,

    /**
     * Bar height at the top row (footer edge), as a fraction of cell height.
     * Thin slivers on dark canvas = reads as dark near the footer.
     */
    MIN_BAR_HEIGHT: 0.01,

    /**
     * Bar height at the bottom row, as a fraction of cell height.
     * Thick blocks on dark canvas = reads as light at the page bottom.
     */
    MAX_BAR_HEIGHT: 1,

    /**
     * Height ramp curve (1 = linear, >1 = holds thin bars longer then opens up toward the bottom).
     */
    HEIGHT_POWER: 1.4,

    /** Top fraction of rows pinned to min bar height. */
    SOLID_TOP: 0.08,

    /** Bottom fraction of rows pinned to max bar height. */
    SOLID_BOTTOM: 0.08,

    /** Vertical inset inside each cell (0–0.4). */
    ROW_GAP: 0,

    /** Horizontal inset inside each column (0–0.2). */
    COL_GAP: 0,

    /** Bar width as a fraction of column width (1 = full column). */
    BAR_WIDTH: 1,

    /**
     * Vertical stack offset per column in row units — the waving stagger.
     * Applied as (row + stagger) slot index.
     */
    STAGGER: 0.0,

    /** Animated wave speed (radians per second). */
    WAVE_SPEED: 0,

    /** Animated ripple added to stagger, in row units. */
    WAVE_AMPLITUDE: 0.05,

    /** Phase spread between adjacent columns (radians). */
    COLUMN_PHASE: 2,

    /** Cap device pixel ratio for performance. */
    MAX_DPR: 2,

    /** Pause animation when band is off-screen. */
    PAUSE_WHEN_OFFSCREEN: true,

    /** 2D Perlin noise — organic variation on bar height and stagger. */
    NOISE: {
      /** Height perturbation strength (fraction of cell height, 0 = off). */
      AMOUNT: 0.8,

      /** Stagger perturbation strength (row units, 0 = off). */
      STAGGER: 0.12,

      /** Spatial scale — lower = larger blobs, higher = finer grain. */
      SCALE: 0.2,

      /** Noise drift speed over time. */
      SPEED: 0.2,

      /** Seed offset — change to reshuffle the noise field. */
      SEED: 0,
    },
  };

  /** Ken Perlin permutation table (deterministic). */
  var PERM = new Uint8Array(512);
  (function initPerm() {
    var source = [
      151, 160, 137, 91, 90, 15, 131, 13, 201, 95, 96, 53, 194, 233, 7, 225, 140, 36, 103, 30,
      69, 142, 8, 99, 37, 240, 21, 10, 23, 190, 6, 148, 247, 120, 234, 75, 0, 26, 197, 62, 94,
      252, 219, 203, 117, 35, 11, 32, 57, 177, 33, 88, 237, 149, 56, 87, 174, 20, 125, 136, 171,
      168, 68, 175, 74, 165, 71, 134, 139, 48, 27, 166, 77, 146, 158, 231, 83, 111, 229, 122, 60,
      211, 133, 230, 220, 105, 92, 41, 55, 46, 245, 40, 244, 102, 143, 54, 65, 25, 63, 161, 1,
      216, 80, 73, 209, 76, 132, 187, 208, 89, 18, 169, 200, 196, 135, 130, 116, 188, 159, 86,
      164, 100, 109, 198, 173, 186, 3, 64, 52, 217, 226, 250, 124, 123, 5, 202, 38, 147, 118,
      126, 255, 82, 85, 212, 207, 206, 59, 227, 47, 16, 58, 17, 182, 189, 28, 42, 223, 183, 170,
      213, 119, 248, 152, 2, 44, 154, 163, 70, 221, 153, 101, 155, 167, 43, 172, 9, 129, 22, 39,
      253, 19, 98, 108, 110, 79, 113, 224, 232, 178, 185, 112, 104, 218, 246, 97, 228, 251, 34,
      242, 193, 238, 210, 144, 12, 191, 179, 162, 241, 81, 51, 145, 235, 249, 14, 239, 107, 49,
      192, 214, 31, 181, 199, 106, 157, 184, 84, 204, 176, 115, 121, 50, 45, 127, 4, 150, 254,
      138, 236, 205, 93, 222, 114, 67, 29, 24, 72, 243, 141, 128, 195, 78, 66, 215, 61, 156, 180,
    ];
    for (var i = 0; i < 512; i++) PERM[i] = source[i & 255];
  })();

  function fade(t) {
    return t * t * t * (t * (t * 6 - 15) + 10);
  }

  function grad2D(hash, x, y) {
    var h = hash & 3;
    var u = h < 2 ? x : y;
    var v = h < 2 ? y : x;
    return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
  }

  /** Returns roughly -1…1 */
  function perlin2(x, y) {
    var xi = Math.floor(x) & 255;
    var yi = Math.floor(y) & 255;
    var xf = x - Math.floor(x);
    var yf = y - Math.floor(y);
    var u = fade(xf);
    var v = fade(yf);
    var aa = PERM[xi] + yi;
    var ab = PERM[xi] + yi + 1;
    var ba = PERM[xi + 1] + yi;
    var bb = PERM[xi + 1] + yi + 1;

    var x1 = lerp(grad2D(PERM[aa], xf, yf), grad2D(PERM[ba], xf - 1, yf), u);
    var x2 = lerp(grad2D(PERM[ab], xf, yf - 1), grad2D(PERM[bb], xf - 1, yf - 1), u);

    return lerp(x1, x2, v);
  }

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function readCanvasColor(root, tokenName) {
    var styles = getComputedStyle(root);
    var raw = styles.getPropertyValue(tokenName).trim();

    if (raw && /^rgba?\(/i.test(raw)) {
      return raw;
    }

    var probe = document.createElement('span');
    probe.hidden = true;
    probe.style.color = 'var(' + tokenName + ')';
    root.appendChild(probe);
    var resolved = getComputedStyle(probe).color;
    probe.remove();
    return resolved;
  }

  function readColors(root) {
    return {
      dark: readCanvasColor(root, '--bhcr-pattern-band-dark'),
      light: readCanvasColor(root, '--bhcr-pattern-band-light'),
    };
  }

  function getColumnCount() {
    var cols = CONFIG.COLS;
    if (typeof cols === 'number') {
      return cols > 0 ? cols : 11;
    }

    var breakpoints = CONFIG.BREAKPOINTS || {};
    var mobileMax = breakpoints.MOBILE_MAX || 768;
    var tabletMax = breakpoints.TABLET_MAX || 990;
    var largeMax = breakpoints.LARGE_MAX || 1369;
    var width = window.innerWidth;

    if (width <= mobileMax) return cols.MOBILE || 3;
    if (width <= tabletMax) return cols.TABLET || 5;
    if (width <= largeMax) return cols.LARGE || 9;
    return cols.DESKTOP || 11;
  }

  function PatternBand(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-bhcr-pattern-canvas]');
    this.ctx = this.canvas.getContext('2d', { alpha: false });
    this.colors = readColors(root);
    this.time = 0;
    this.running = false;
    this.visible = true;
    this.width = 0;
    this.height = CONFIG.HEIGHT;
    this.cols = 0;
    this.rows = 0;
    this.dpr = 1;
    this._onResize = this._onResize.bind(this);
    this._tick = this._tick.bind(this);
    this._observer = null;
  }

  PatternBand.prototype._applyLayout = function () {
    this.root.style.setProperty('--bhcr-pattern-band-height', CONFIG.HEIGHT + 'px');
  };

  PatternBand.prototype._onResize = function () {
    this._applyLayout();

    var rect = this.root.getBoundingClientRect();
    var cssWidth = Math.max(1, Math.round(rect.width));
    var cssHeight = Math.max(1, Math.round(CONFIG.HEIGHT));
    this.dpr = Math.min(window.devicePixelRatio || 1, CONFIG.MAX_DPR);
    this.width = cssWidth;
    this.height = cssHeight;
    this.cols = getColumnCount();
    this.rows = Math.max(1, Math.round(cssHeight / CONFIG.CELL_SIZE));
    this.canvas.width = Math.round(cssWidth * this.dpr);
    this.canvas.height = Math.round(cssHeight * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.ctx.imageSmoothingEnabled = false;

    if (this.running && !reducedMotion.matches) {
      this._draw();
    }
  };

  PatternBand.prototype._columnStagger = function (col) {
    var wave =
      Math.sin(col * CONFIG.COLUMN_PHASE + this.time * CONFIG.WAVE_SPEED) *
      CONFIG.WAVE_AMPLITUDE;

    return col * CONFIG.STAGGER + wave;
  };

  PatternBand.prototype._perlinSample = function (col, row) {
    var noise = CONFIG.NOISE;
    if (!noise) return 0;

    var amount = noise.AMOUNT || 0;
    var stagger = noise.STAGGER || 0;
    if (amount <= 0 && stagger <= 0) return 0;

    var scale = noise.SCALE || 0.085;
    var speed = noise.SPEED || 0;
    var seed = noise.SEED || 0;
    var nx = col * scale + seed;
    var ny = row * scale - this.time * speed;

    return perlin2(nx, ny);
  };

  PatternBand.prototype._barHeightFactor = function (row) {
    var rows = this.rows;
    var rowNorm = row / Math.max(1, rows - 1);

    if (rowNorm <= CONFIG.SOLID_TOP) return CONFIG.MIN_BAR_HEIGHT;
    if (rowNorm >= 1 - CONFIG.SOLID_BOTTOM) return CONFIG.MAX_BAR_HEIGHT;

    var span = 1 - CONFIG.SOLID_TOP - CONFIG.SOLID_BOTTOM;
    var t = (rowNorm - CONFIG.SOLID_TOP) / span;

    return lerp(
      CONFIG.MIN_BAR_HEIGHT,
      CONFIG.MAX_BAR_HEIGHT,
      Math.pow(clamp(t, 0, 1), CONFIG.HEIGHT_POWER)
    );
  };

  PatternBand.prototype._columnBounds = function (col) {
    var cols = this.cols;
    var width = this.width;
    var x0 = Math.floor((col * width) / cols);
    var x1 = Math.floor(((col + 1) * width) / cols);

    return {
      x: x0,
      w: Math.max(1, x1 - x0),
    };
  };

  PatternBand.prototype._draw = function () {
    this.colors = readColors(this.root);

    var cols = this.cols;
    var rows = this.rows;
    var ctx = this.ctx;
    var cellH = this.height / rows;
    var rowGap = clamp(CONFIG.ROW_GAP, 0, 0.4);
    var colGap = clamp(CONFIG.COL_GAP, 0, 0.2);
    var barWidth = clamp(CONFIG.BAR_WIDTH, 0.1, 1);

    ctx.fillStyle = this.colors.dark;
    ctx.fillRect(0, 0, this.width, this.height);
    ctx.fillStyle = this.colors.light;

    for (var col = 0; col < cols; col++) {
      var bounds = this._columnBounds(col);
      var barW;
      var x;

      if (barWidth >= 1 && colGap <= 0) {
        x = bounds.x;
        barW = bounds.w;
      } else {
        barW = Math.max(1, Math.round(bounds.w * barWidth * (1 - colGap)));
        x = bounds.x + Math.round((bounds.w - barW) * 0.5);
      }

      var stagger = this._columnStagger(col);

      for (var row = 0; row < rows; row++) {
        var noise = this._perlinSample(col, row);
        var noiseCfg = CONFIG.NOISE || {};
        var heightFactor =
          this._barHeightFactor(row) * (1 - rowGap) + noise * (noiseCfg.AMOUNT || 0);
        heightFactor = clamp(heightFactor, 0, 1);
        var barH = Math.max(1, cellH * heightFactor);
        var slot = row + stagger + noise * (noiseCfg.STAGGER || 0);
        var y = slot * cellH + (cellH - barH);

        if (y + barH < 0 || y > this.height) continue;

        ctx.fillRect(x, y, barW, barH);
      }
    }
  };

  PatternBand.prototype._tick = function (now) {
    if (!this.running) return;

    if (!reducedMotion.matches) {
      this.time = now * 0.001;
      if (this.visible) this._draw();
    }

    window.requestAnimationFrame(this._tick);
  };

  PatternBand.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this._onResize();
    window.requestAnimationFrame(this._tick);
  };

  PatternBand.prototype.stop = function () {
    this.running = false;
  };

  PatternBand.prototype.destroy = function () {
    this.stop();
    window.removeEventListener('resize', this._onResize);

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  };

  PatternBand.prototype.init = function () {
    this._applyLayout();
    this._onResize();

    var resizeObserver = window.ResizeObserver;
    if (resizeObserver) {
      this._observer = new resizeObserver(this._onResize);
      this._observer.observe(this.root);
    } else {
      window.addEventListener('resize', this._onResize);
    }

    if (CONFIG.PAUSE_WHEN_OFFSCREEN && 'IntersectionObserver' in window) {
      var visibilityObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (entry) {
              this.visible = entry.isIntersecting;
            }.bind(this)
          );
        }.bind(this),
        { rootMargin: '120px 0px' }
      );
      visibilityObserver.observe(this.root);
    }

    if (reducedMotion.matches) {
      this.time = 0;
      this._draw();
      return;
    }

    this.start();
  };

  function initPatternBands(scope) {
    var rootScope = scope || document;
    rootScope.querySelectorAll('[data-bhcr-pattern-band]').forEach(function (el) {
      if (el.__bhcrPatternBand) return;
      var band = new PatternBand(el);
      band.init();
      el.__bhcrPatternBand = band;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initPatternBands();
    });
  } else {
    initPatternBands();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initPatternBands(event.target);
  });
})();
